import { Files, type AttachmentView } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { Icon, IconButton, Image, Progress, Stack, Text } from "../primitives/index.ts";

export type AttachmentChipsProps = {
  attachments: AttachmentView[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  testID: string;
};

/** The draft's files above the composer field: thumbnail or icon, name, upload progress, retry and remove. */
export function AttachmentChips({ attachments, onRemove, onRetry, testID }: AttachmentChipsProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  if (attachments.length === 0) return null;
  return (
    <Stack direction="horizontal" gap="2" wrap testID={testID}>
      {attachments.map((a) => {
        const failed = a.phase === "failed";
        const status = a.phase === "done" ? Files.formatFileSize(a.size, locale) : tr(a.label);
        return (
          <Stack key={a.id} direction="horizontal" align="center" gap="2" padding="1.5" radius="md" border={failed ? "status.danger" : "border.subtle"} background="bg.surface" testID={`${testID}-${a.id}`}>
            {a.previewUrl !== undefined && a.kind === "image" ? (
              <Image src={a.previewUrl} alt={a.name} width={40} height={40} radius="sm" />
            ) : (
              <Stack padding="2" radius="sm" background="accent.subtle">
                <Icon name={a.icon as IconName} size="icon.md" color="accent.text" />
              </Stack>
            )}
            <Stack gap="0.5">
              <Text variant="footnote" weight="medium" lines={1}>
                {a.name}
              </Text>
              <Text variant="caption" color={failed ? "status.danger" : "text.tertiary"} lines={1} testID={`${testID}-${a.id}-status`}>
                {status}
              </Text>
              {a.phase === "uploading" || a.phase === "queued" ? (
                <Progress value={a.phase === "queued" ? 0 : a.progress} accessibilityLabel={tr(a.label)} testID={`${testID}-${a.id}-progress`} />
              ) : null}
            </Stack>
            {failed ? (
              <IconButton icon="reload" size="sm" accessibilityLabel={t("files.upload.retry")} onPress={() => onRetry(a.id)} testID={`${testID}-${a.id}-retry`} />
            ) : null}
            <IconButton icon="close" size="sm" accessibilityLabel={t("files.attach.remove", { name: a.name })} onPress={() => onRemove(a.id)} testID={`${testID}-${a.id}-remove`} />
          </Stack>
        );
      })}
    </Stack>
  );
}
